import { alertService } from '@/services/alertService';

const state = {
  alerts: [],        // Unread alerts shown in the bell dropdown
  unreadCount: 0,    // Badge counter — kept in sync via HTTP
  loading: false,
  error: null,
  isConnected: false, // WebSocket connection status
};

const getters = {
  alerts: (state) => state.alerts || [],
  unreadCount: (state) => state.unreadCount || 0,
  isLoading: (state) => state.loading || false,
  error: (state) => state.error || null,
  isConnected: (state) => state.isConnected || false,
};

const mutations = {
  SET_ALERTS(state, alerts) {
    state.alerts = alerts || [];
  },

  // Mark a single alert as read in the local list (optimistic update for the dropdown)
  MARK_ALERT_READ(state, alertId) {
    const alert = (state.alerts || []).find((a) => a.id === alertId);
    if (alert) {
      alert.userStatus = { ...alert.userStatus, isRead: true, readAt: new Date() };
    }
  },

  SET_UNREAD_COUNT(state, count) {
    state.unreadCount = count || 0;
  },

  DECREMENT_UNREAD_COUNT(state) {
    if (state.unreadCount > 0) {
      state.unreadCount--;
    }
  },

  SET_LOADING(state, loading) {
    state.loading = loading || false;
  },

  SET_ERROR(state, error) {
    state.error = error || null;
  },

  CLEAR_ERROR(state) {
    state.error = null;
  },

  SET_CONNECTION_STATUS(state, isConnected) {
    state.isConnected = isConnected || false;
  },
};

const actions = {
  /**
   * Fetch the last `limit` unread alerts.
   * Called when the user opens the AlertBell dropdown.
   */
  async fetchAlerts({ commit }, { limit = 10 } = {}) {
    try {
      commit('SET_LOADING', true);
      commit('CLEAR_ERROR');

      const response = await alertService.getAlertsPaginated({
        limit,
        readingStatus: 'UNREAD',
        sortBy: 'createdAt',
        sortOrder: 'desc',
      });

      const alertsData = response.items || response.data || response;

      if (!Array.isArray(alertsData)) {
        throw new Error('Invalid response format: expected an array of alerts');
      }

      // Normalise dates and ensure userStatus exists
      const processed = alertsData.map((alert) => ({
        ...alert,
        createdAt: new Date(alert.createdAt),
        resolvedAt: alert.resolvedAt ? new Date(alert.resolvedAt) : null,
        expiresAt: alert.expiresAt ? new Date(alert.expiresAt) : null,
        userStatus: alert.userStatus || { isRead: false, readAt: null },
      }));

      commit('SET_ALERTS', processed);
      return response;
    } catch (error) {
      console.error('Failed to fetch alerts:', error);
      commit('SET_ERROR', error.message || 'Failed to fetch alerts');
      throw error;
    } finally {
      commit('SET_LOADING', false);
    }
  },

  /**
   * Fetch the current unread count from the server.
   * Called on init and whenever the WebSocket emits 'new-alert'.
   */
  async fetchUnreadCount({ commit }) {
    try {
      const response = await alertService.getUnreadCount();

      if (response.success && response.data) {
        commit('SET_UNREAD_COUNT', response.data.unreadCount || 0);
        return response.data.unreadCount || 0;
      }

      // Unexpected response shape — set to 0 so badge doesn't show stale data
      commit('SET_UNREAD_COUNT', 0);
      return 0;
    } catch (error) {
      console.error('Failed to fetch unread count:', error);
      // Don't commit an error — the badge will keep showing its last known value
      return 0;
    }
  },

  /**
   * Mark a single alert as read.
   * Optimistically hides the alert in the dropdown, then calls the API.
   * Refreshes the unread count from the server afterward.
   */
  async markAsRead({ commit, dispatch }, alertId) {
    // Optimistic update: mark as read in the local list so it fades out immediately
    commit('MARK_ALERT_READ', alertId);

    try {
      await alertService.markAsRead(alertId);

      // Sync the badge count with the server after the read
      await dispatch('fetchUnreadCount');
    } catch (error) {
      console.error('Failed to mark alert as read:', error);
      commit('SET_ERROR', error.message || 'Failed to mark alert as read');
      throw error;
    }
  },

  // Update WebSocket connection status (called by websocketService)
  setConnectionStatus({ commit }, isConnected) {
    commit('SET_CONNECTION_STATUS', isConnected);
  },

  clearError({ commit }) {
    commit('CLEAR_ERROR');
  },

  setError({ commit }, error) {
    commit('SET_ERROR', error);
  },
};

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
};
